import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseEmbeddedTabsProps {
  defaultIndex?: number;
  selectedIndex?: number;
  onTabChange?: (index: number) => void;
}

export interface UseEmbeddedTabsReturn {
  activeTab: number;
  setActiveTab: (index: number) => void;
  getTabListProps: () => {
    role: string;
  };
  getTabProps: (index: number) => {
    role: string;
    'aria-selected': boolean;
    tabIndex: number;
    onClick: () => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
  getTabPanelProps: (index: number) => {
    role: string;
    id: string;
    'aria-labelledby': string;
    hidden: boolean;
  };
  registerTab: (index: number) => void;
  totalTabs: number;
}

/**
 * useEmbeddedTabs Hook
 *
 * Manages tab state, keyboard navigation, and accessibility for embedded tab systems.
 * Based on WAI-ARIA Tabs Pattern with keyboard support.
 *
 * Features:
 * - Controlled/Uncontrolled mode support
 * - Keyboard navigation (Arrow keys, Home, End)
 * - Full ARIA attribute support
 * - Tab registration tracking
 *
 * @example
 * const { activeTab, getTabListProps, getTabProps, getTabPanelProps } = useEmbeddedTabs({
 *   defaultIndex: 0,
 *   onTabChange: (index) => console.log('Tab changed to', index)
 * });
 */
export function useEmbeddedTabs({
  defaultIndex = 0,
  selectedIndex,
  onTabChange,
}: UseEmbeddedTabsProps = {}): UseEmbeddedTabsReturn {
  // Track registered tabs
  const registeredTabs = useRef<Set<number>>(new Set());
  const totalTabsRef = useRef(0);

  // State management - support controlled and uncontrolled mode
  const [internalActiveTab, setInternalActiveTab] = useState(defaultIndex);
  const activeTab = selectedIndex !== undefined ? selectedIndex : internalActiveTab;

  const setActiveTab = useCallback(
    (index: number) => {
      setInternalActiveTab(index);
      onTabChange?.(index);
    },
    [onTabChange]
  );

  // Register a tab
  const registerTab = useCallback((index: number) => {
    registeredTabs.current.add(index);
    totalTabsRef.current = Math.max(totalTabsRef.current, index + 1);
  }, []);

  // TabList props
  const getTabListProps = useCallback(() => {
    return {
      role: 'tablist',
    };
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (currentIndex: number) => (e: React.KeyboardEvent) => {
      const tabs = Array.from(registeredTabs.current).sort((a, b) => a - b);
      const currentPosition = tabs.indexOf(currentIndex);

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          if (currentPosition > 0) {
            setActiveTab(tabs[currentPosition - 1]);
          }
          break;

        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          if (currentPosition < tabs.length - 1) {
            setActiveTab(tabs[currentPosition + 1]);
          }
          break;

        case 'Home':
          e.preventDefault();
          setActiveTab(tabs[0]);
          break;

        case 'End':
          e.preventDefault();
          setActiveTab(tabs[tabs.length - 1]);
          break;

        default:
          break;
      }
    },
    [setActiveTab]
  );

  // Individual Tab props
  const getTabProps = useCallback(
    (index: number) => {
      const isActive = activeTab === index;

      return {
        role: 'tab',
        'aria-selected': isActive,
        tabIndex: isActive ? 0 : -1,
        onClick: () => setActiveTab(index),
        onKeyDown: handleKeyDown(index),
      };
    },
    [activeTab, setActiveTab, handleKeyDown]
  );

  // TabPanel props
  const getTabPanelProps = useCallback(
    (index: number) => {
      const isActive = activeTab === index;

      return {
        role: 'tabpanel',
        id: `tab-panel-${index}`,
        'aria-labelledby': `tab-${index}`,
        hidden: !isActive,
      };
    },
    [activeTab]
  );

  return {
    activeTab,
    setActiveTab,
    getTabListProps,
    getTabProps,
    getTabPanelProps,
    registerTab,
    totalTabs: totalTabsRef.current,
  };
}
